import React from 'react';
import { FormSpy, AnyObject, FieldInputProps } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import { AccordionSummary, Typography, AccordionDetails, Grid } from '@material-ui/core';
import { IDownShiftItem } from 'mui-downshift';
import { KeyboardDateTimePicker } from '@material-ui/pickers/DateTimePicker/DateTimePicker';
import { ReminderSelector } from '../../../components/ReminderSelector';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker/DatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import moment from 'moment';
import UserSelector from '../../../components/UserSelector';
import type { EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('AssignmentPanel');

// Explicit request (EnquiryFormDrawer split): the "Assignment" accordion, moved out of the drawer's inline
// getAssignment render function with its JSX and every field's name / validate / handler logic unchanged.
// The outer component subscribes to the form values; the memoised body only re-renders when a value it
// displays (or one its validators read) changes. The "follow up due" validator / required flag are worked
// out in the outer component from the complete values (they also read the current tab and the form's
// field state), and event handlers read form.getState().values when they fire.
export interface AssignmentPanelProps {
    form: FormApi<any>;
    classes: EnquiryFormClasses;
    // tslint:disable-next-line: no-any
    enquiryActionData: any;
    isAssignPanelExpand: boolean;
    isActivityTab: boolean;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    requiredValidator: FieldValidator<any>;
    requiredDateValidator: FieldValidator<any>;
    dateValidator: FieldValidator<any>;
    // tslint:disable-next-line: no-any
    onDropDownSelect: (form: FormApi<any>, values: AnyObject, input: FieldInputProps<any, HTMLElement>, selection: any, name: string) => void;
    getOutcomeDefaultTime: () => number;
    followUpDueDateValidation: (form: FormApi<any>, values: AnyObject) => FieldValidator<any> | undefined;
    followUpDueDateRequired: (form: FormApi<any>, values: AnyObject) => boolean;
}

interface AssignmentPanelBodyProps extends AssignmentPanelProps {
    formValues: AnyObject;
    followUpDueDateValidator: FieldValidator<any> | undefined;
    isFollowUpDueDateRequired: boolean;
}

const AssignmentPanelBody: React.FC<AssignmentPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, enquiryActionData, isAssignPanelExpand, isActivityTab, onExpandChange, requiredValidator, requiredDateValidator, dateValidator,
        onDropDownSelect, getOutcomeDefaultTime, formValues, followUpDueDateValidator, isFollowUpDueDateRequired,
    } = bodyProps;
    const values = formValues;

    // Explicit request: which of these rules apply is read from the values this panel is given, not from the form's own field state. That returned
    // nothing while a field was not registered yet (the panel is rebuilt when the layout changes, e.g. an area of law with custom fields), and
    // nothing !== null is true - so Assigned To (and the rest) were required on an enquiry with no assignment. The rule is also only worked out
    // when the panel renders, so it has to be right on the first render. Not set means null or undefined.
    const isFollowUpDueDateSet = values.followUpDueDate !== null && values.followUpDueDate !== undefined;
    const isAssignedToSet = values.assignedTo !== null && values.assignedTo !== undefined;
    const isNotProceedingChosen = values.isNotProceeding === true;

    let defaultExpanded = enquiryActionData && !enquiryActionData.canAssign
        ? false
        : enquiryActionData?.canFollowUp ? false : true;
    return (
        <Accordion
            data-tour="enquiry-assignment"
            defaultExpanded={defaultExpanded}
            expanded={isAssignPanelExpand === true ? isAssignPanelExpand : defaultExpanded}
            onChange={onExpandChange}
            className={classes.expansionPanel}
            disabled={enquiryActionData ? !enquiryActionData.canAssign : false}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="assignment-content"
                id="assignment-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipGreen)}>
                        <AssignmentIndIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelGreen, classes.sectionLabelText)}>
                        Assignment
                    </Typography>
                </div>
                {
                    values ? (
                        <div className={'col'}>
                            {values.assignedToName
                                ? (
                                    'Assigned to ' + values.assignedToName +
                                    (
                                        values.dateAssigned && values.dateAssigned !== null ? (
                                            ' on ' + moment(values.dateAssigned).format('DD MMM YYYY')
                                        ) : ''
                                    )
                                ) : ''
                            }
                        </div>
                    ) : null
                }
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={5} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div>
                            <Field
                                name="assignedTo"
                                validate={!isActivityTab
                                    ? isFollowUpDueDateSet
                                        ? (isNotProceedingChosen ? undefined : requiredValidator)
                                        : undefined
                                    : undefined}
                            >
                                {({ input, meta }) => (
                                    <div>
                                        {/* <RenderCount/> */}
                                        <UserSelector
                                            {...input}
                                            variant="outlined"
                                            className={classes.userSelectorDense}
                                            label="Assigned To"
                                            labelProps={{ shrink: true }}
                                            name="assignedTo"
                                            required={!isActivityTab
                                                ? isFollowUpDueDateSet
                                                    ? (isNotProceedingChosen ? false : true) : false
                                                : false}
                                            // tslint:disable-next-line: max-line-length
                                            onSelection={(selection: IDownShiftItem, name: string) => {
                                                // Another approach.
                                                onDropDownSelect(form, form.getState().values, input, selection, name);
                                            }}
                                            isGuidAsValue={true}
                                            // isStaffOnly={true}
                                            displayLabel={values.assignedToName === null
                                                ? undefined : values.assignedToName}
                                            isExternal={false}
                                            isAuthorized={true}
                                        />
                                    </div>
                                )}
                            </Field>
                        </div>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div>
                            <Field
                                name="followUpDueDate"
                                // tslint:disable-next-line: max-line-length
                                validate={followUpDueDateValidator}
                                subscription={{ touched: true, error: true, value: true }}
                            >
                                {({ input, meta }) => (
                                    <div>
                                        <KeyboardDateTimePicker
                                            inputVariant="outlined"
                                            size="small"
                                            {...input}
                                            className={classes.textField}
                                            label="Follow Up Due"
                                            format="DD/MM/YYYY hh:mm a"
                                            placeholder="dd/mm/yyyy hh:mm a"
                                            required={isFollowUpDueDateRequired}
                                            animateYearScrolling={true}
                                            // tslint:disable-next-line: max-line-length
                                            autoOk={true}
                                            allowKeyboardControl={true}
                                            variant={'inline'}
                                            error={meta.error && meta.touched}
                                            helperText={
                                                meta.error && meta.touched
                                                    ? meta.error // 'Follow Up Due date is required'
                                                    : ''
                                            }
                                            inputProps={{ autoComplete: 'off' }}
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            value={values.followUpDueDate}
                                            // minDate={new Date()}
                                            initialFocusedDate={getOutcomeDefaultTime()}
                                        />
                                    </div>
                                )}
                            </Field>
                        </div>
                    </Grid>
                    <Grid item={true} xs={3} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="assignmentFollowUpDueReminder"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div className={classes.selector}>
                                    <ReminderSelector
                                        {...input}
                                        label="Reminder"
                                        variant="outlined"
                                        size="small"
                                        className={classes.textField}
                                        onSelection={(selection: IAutoCompleteItem) => {
                                            input.onChange(selection);
                                        }}
                                        error={meta.error && meta.touched}
                                        helperText={
                                            meta.error &&
                                                meta.touched
                                                ? meta.error
                                                : ''
                                        }
                                        showIcon={true}
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div>
                            <Field
                                name="assignedBy"
                                validate={!isActivityTab
                                    ? isAssignedToSet ? requiredValidator : undefined
                                    : undefined}
                            >
                                {({ input, meta }) => (
                                    <div>
                                        {/* <RenderCount/> */}
                                        <UserSelector
                                            {...input}
                                            variant="outlined"
                                            className={classes.userSelectorDense}
                                            label="Assigned By"
                                            labelProps={{ shrink: true }}
                                            name="assignedBy"
                                            // tslint:disable-next-line: max-line-length
                                            required={!isActivityTab
                                                ? isAssignedToSet ? true : undefined
                                                : false}
                                            // tslint:disable-next-line: max-line-length
                                            onSelection={(selection: IDownShiftItem, name: string) => {
                                                // input.onChange(selection.value);
                                                // Another approach.
                                                onDropDownSelect(form, form.getState().values, input, selection, name);
                                            }}
                                            value={values.assignedBy}
                                            displayLabel={values.assignedByName}
                                            isGuidAsValue={true}
                                            isAuthorized={true}
                                            isExternal={false}
                                        // isStaffOnly={true}
                                        />
                                    </div>
                                )}
                            </Field>
                        </div>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div>
                            <Field
                                name="dateAssigned"
                                // tslint:disable-next-line: max-line-length
                                validate={!isActivityTab
                                    ? isAssignedToSet ? requiredDateValidator : dateValidator
                                    : undefined}
                                subscription={{ touched: true, error: true, value: true }}
                            >
                                {({ input, meta }) => (
                                    <div>
                                        {/* tslint:disable-next-line: no-console */}
                                        {/* {console.log({...input})} */}
                                        <KeyboardDatePicker
                                            inputVariant="outlined"
                                            size="small"
                                            {...input}
                                            className={classes.textField + ' active'}
                                            id="dateAssigned"
                                            name="dateAssigned"
                                            label="Date Assigned"
                                            format="DD/MM/YYYY"
                                            placeholder="dd/mm/yyyy"
                                            // tslint:disable-next-line: max-line-length
                                            required={!isActivityTab
                                                ? isAssignedToSet ? true : undefined
                                                : undefined}
                                            animateYearScrolling={true}
                                            // tslint:disable-next-line: max-line-length
                                            autoOk={true}
                                            allowKeyboardControl={false}
                                            variant={'inline'}
                                            error={meta.error && meta.touched}
                                            helperText={meta.error}
                                            inputProps={{ autoComplete: 'off' }}
                                            InputLabelProps={{ shrink: true }}
                                            onChange={(date: MaterialUiPickersDate | null, value?: string | null) => input.onChange(date)}
                                        />
                                    </div>
                                )}
                            </Field>
                        </div>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
AssignmentPanelBody.displayName = 'AssignmentPanelBody';

export const AssignmentPanel: React.FC<AssignmentPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <AssignmentPanelBody
                {...panelProps}
                followUpDueDateValidator={panelProps.followUpDueDateValidation(panelProps.form, values)}
                isFollowUpDueDateRequired={panelProps.followUpDueDateRequired(panelProps.form, values)}
                formValues={{
                    assignedToName: values.assignedToName,
                    dateAssigned: values.dateAssigned,
                    followUpDueDate: values.followUpDueDate,
                    assignedBy: values.assignedBy,
                    assignedByName: values.assignedByName,
                    assignedTo: values.assignedTo,
                    isNotProceeding: values.isNotProceeding,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
AssignmentPanel.displayName = 'AssignmentPanel';
